import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, FormikHelpers, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { validateEmailTls } from '../../utils/checkTLSUtil';
import {
  APECode,
  CompanyCreationDate,
  CompanyName,
  CountryCodeMobilePhone,
  Email,
  LegalEntity,
  MobilePhone,
  TelephonePhone,
} from '../../utils/fieldsBusinessCustomer';
import {
  CustomerDetailsFormGroupForms,
  CustomerDetailsFormGroupType,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { GeneralInfoDataFormType, getGeneralInfoDataFormInitialValues } from './initialValues';
import { generalInfoDataFormRequestMapper } from './requestMapper';
import { getGeneralInfoDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'business'>;
  formGroup: CustomerDetailsFormGroupType;
}

export const GeneralInfoDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [initialValues, setInitialValues] = useState({} as GeneralInfoDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<GeneralInfoDataFormType>>();

  const creditApplicantCustomerData = useSelector(creditApplicationSelectors.selectCreditApplicantCustomerData) as
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getGeneralInfoDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getGeneralInfoDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  const onSubmit = async (
    values: GeneralInfoDataFormType,
    { setFieldError }: FormikHelpers<GeneralInfoDataFormType>
  ) => {
    const tlsValid = await validateEmailTls(values.email!, setFieldError, dispatch);

    if (!tlsValid) {
      return;
    }

    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(generalInfoDataFormRequestMapper(values, creditApplicantCustomerData))
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:contactInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm, initialValues, validateForm } =
    useFormikContext<GeneralInfoDataFormType>();

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3" m="1/2" s="1/1">
            <LegalEntity t={t} setFieldValue={setFieldValue} values={values} isDisabled />
          </Layout.Item>
          <Layout.Item default="1/6" m="1/2" s="1/1">
            <APECode t={t} isDisabled />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <CompanyName t={t} isDisabled />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <CompanyCreationDate
              t={t}
              setFieldValue={setFieldValue}
              values={values}
              label={t('step4:companyCreationDate:label')}
              infoText={t('step4:dateInfoText:label')}
              isDisabled
            />
          </Layout.Item>
          <Layout.Item default="1/3" m="1/2" s="1/1">
            <Email t={t} />
          </Layout.Item>
          <Layout.Item default="1/6" m="1/2" s="1/1">
            <CountryCodeMobilePhone t={t} setFieldValue={setFieldValue} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <MobilePhone t={t} setFieldValue={setFieldValue} values={values} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <TelephonePhone t={t} setFieldValue={setFieldValue} />
          </Layout.Item>
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
